/**
 * DeleteConfirmDialog Component
 *
 * Modal confirmation gate for file deletion, built on the Coss AlertDialog
 * primitive (Base UI): portal + focus trap + Escape dismiss come for free.
 * As an alert dialog it is intentionally NOT dismissible by an outside/backdrop
 * click — a destructive action must be confirmed or explicitly cancelled.
 */

import { useTranslation } from "react-i18next";
import { TriangleAlertIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface DeleteConfirmDialogProps {
  filename: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  error?: string | null;
  isDeleting?: boolean;
}

export function DeleteConfirmDialog({
  filename,
  onConfirm,
  onCancel,
  error,
  isDeleting,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation("kb");

  if (!filename) return null;

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <AlertDialogPopup data-test="delete-confirm-dialog">
        <AlertDialogHeader>
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/8 text-destructive-foreground">
            <TriangleAlertIcon aria-hidden="true" />
          </div>
          <AlertDialogTitle>
            {t("deleteDialog.title", { filename })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("deleteDialog.warning")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div
            className="mx-6 mb-4 rounded-md border border-destructive/32 bg-destructive/8 px-3 py-2 text-destructive-foreground text-sm"
            data-test="delete-confirm-error"
            role="alert"
          >
            {error}
          </div>
        )}

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1"
            data-test="delete-confirm-cancel"
          >
            {t("deleteDialog.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1"
            data-test="delete-confirm-confirm"
          >
            {t("deleteDialog.confirm")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
